import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import { Secret } from "@coeff/api";
import { COLORS } from "@coeff/utils";
import { useMutation } from "@tanstack/react-query";
import { Input, Upload } from "antd";
// @ts-ignore
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-sql";
import React, { useState } from "react";
import styled from "styled-components";

import { Button, FlexColumn, Modal, Typography } from "../../../common";
import { useApiClient, useMessage } from "../../../globals/contexts";
import { useAuthContext } from "../../context/AuthContext";
import { AxiosErrorWithType } from "../../utils";

const EditorContainer = styled.div`
  position: relative;
  background: ${COLORS.black4};
  border-radius: 4px;
  padding: 10px;
  pre {
    margin: 0;
  }
`;

const CopyButton = styled(Button)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 4px 8px;
  font-size: 12px;
  height: 24px;
  min-width: 60px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border: none;
    padding-bottom: 0;
    padding-top: 20px;
  }

  .ant-modal-title {
    font-size: 20px;
  }

  .ant-modal-body {
    padding: 20px;
  }
`;

const StyledUpload = styled(Upload)`
  .ant-upload {
    width: 100%;
  }
`;

const StyledList = styled.ol`
  margin-left: 16px;
  margin-top: 8px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WarningIcon = styled(WarningFilled)`
  svg {
    fill: ${COLORS.yellow};
  }
`;

const ErrorText = styled(Typography)`
  margin-top: 8px;
`;

export type SnowflakeKeyPair = {
  privateKey?: Secret;
  publicKey: string;
};

export type SnowflakeKeyPairAction = "generate" | "view" | "disable";

type Props = {
  userName: string;
  keyPair: SnowflakeKeyPair | null;
  action: SnowflakeKeyPairAction;
  onClose: (options: SnowflakeKeyPair | null) => void;
};

enum Step {
  createKeyPair,
  downloadKeyPair,
  enterPassphrase,
  configureKeyPair,
}

const download = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const handleCopy = (textToCopy: string) => {
  const tempTextArea = document.createElement("textarea");
  tempTextArea.value = textToCopy;
  document.body.appendChild(tempTextArea);

  tempTextArea.select();
  document.execCommand("copy");

  document.body.removeChild(tempTextArea);
};

const alterUserRSAKeyQuery = `ALTER USER {{USER_PLACEHOLDER}} SET RSA_PUBLIC_KEY='{{PUBLIC_KEY}}'`;

const disableMfaEnforcementQuery = `CREATE AUTHENTICATION POLICY disable_mfa_enforcement
  MFA_ENROLLMENT = OPTIONAL;

ALTER USER {{USER_PLACEHOLDER}}
  SET AUTHENTICATION POLICY disable_mfa_enforcement;

`;

const ALPHA_NUMERIC_REGEX = /^[a-zA-Z0-9_]+$/;

const hasNonAlphanumeric = (userName: string): boolean => !ALPHA_NUMERIC_REGEX.test(userName);

export const SnowflakeKeyPairModel: React.FC<Props> = ({ userName, keyPair, onClose, action }) => {
  const apiClient = useApiClient();
  const message = useMessage();
  const { sentryCapture } = useAuthContext();

  const [step, setStep] = React.useState<Step>(
    action === "generate" ? Step.createKeyPair : Step.configureKeyPair
  );

  const [snowflakeKey, setSnowflakeKey] = useState<SnowflakeKeyPair | null>(keyPair);

  const [tooltip, setTooltip] = useState<string>("Copy SQL");

  const [passphrase, setPassphrase] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [uploadedPrivateKey, setUploadedPrivateKey] = useState<string>("");

  const generateKeyPairMutation = useMutation(
    async () => {
      const { data } = await apiClient.getSnowflakeKeyPair({
        get_snowflake_key_pair_request: {
          include_private_key: true,
        },
      });
      return data;
    },
    {
      onSuccess: data => {
        setSnowflakeKey({
          privateKey: data.encrypted_private_key,
          publicKey: data.public_key,
        });

        if (data.private_key) {
          download(data.private_key, "coefficient_snowflake_rsa_key.p8");
        }

        setStep(Step.downloadKeyPair);
      },
      onError: (error: Error) => {
        message?.error(error.message);
        sentryCapture?.({
          error,
          name: "SnowflakeKeyPairModel",
        });
      },
    }
  );

  const uploadKeyPairMutation = useMutation(
    async (params: { privateKey: string; passphrase?: string; hasError: boolean }) => {
      setUploadError("");
      const { data } = await apiClient.getSnowflakeKeyPair({
        get_snowflake_key_pair_request: {
          private_key: params.privateKey,
          private_key_passphrase: params.hasError ? params.passphrase || "" : undefined,
        },
      });
      return data;
    },
    {
      onSuccess: data => {
        setUploadError("");
        setSnowflakeKey({
          privateKey: data.encrypted_private_key,
          publicKey: data.public_key,
        });
        setStep(Step.configureKeyPair);
      },
      onError: (error: AxiosErrorWithType) => {
        const data = error.response.data;
        if (data.type !== "snowflake_encrypted_private_key") {
          setUploadError(data.message || "");
        }
        if (
          [
            "snowflake_encrypted_private_key",
            "snowflake_private_key_incorrect_passphrase",
          ].includes(data.type || "")
        ) {
          setStep(Step.enterPassphrase);
          return;
        }
        sentryCapture?.({
          error,
          name: "SnowflakeKeyPairModel",
        });
      },
    }
  );

  const handleFileUpload = async (file: File): Promise<boolean> => {
    try {
      const text = await file.text();
      if (!text.trim()) {
        setUploadError("Private key cannot be empty");
        return false;
      }

      if (text.length > 5000) {
        setUploadError("Private key is too large (max 5KB allowed)");
        return false;
      }

      setUploadedPrivateKey(text);
      setUploadError("");

      uploadKeyPairMutation.mutate({ privateKey: text, hasError: false });
    } catch (error) {
      console.error("Error reading file:", error);
      setUploadError("Error reading file. Please try again.");
      sentryCapture?.({
        error,
        name: "SnowflakeKeyPairModel",
      });
    }
    return false;
  };

  const disabled = generateKeyPairMutation.isLoading || uploadKeyPairMutation.isLoading;

  const query = action === "disable" ? disableMfaEnforcementQuery : alterUserRSAKeyQuery;

  const updatedValue = query
    .replace(
      "{{USER_PLACEHOLDER}}",
      hasNonAlphanumeric(userName) ? `"${userName || "<add user_name here>"}"` : userName
    )
    .replace("{{PUBLIC_KEY}}", snowflakeKey?.publicKey || "<add public key here>");

  const onCopy = () => {
    handleCopy(updatedValue);
    setTooltip("Copied!");
    setTimeout(() => {
      setTooltip("Copy SQL");
    }, 2000);
  };

  if (action === "disable") {
    return (
      <StyledModal visible footer={null} onCancel={() => onClose(snowflakeKey)} centered>
        <FlexColumn gap={1}>
          <Typography fontSize={20} fontWeight={600} lineHeight={"22px"}>
            Disabling MFA in Snowflake
          </Typography>

          <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
            A Snowflake account admin must run the following SQL query to disable MFA for you
          </Typography>

          <EditorContainer>
            <pre
              dangerouslySetInnerHTML={{
                __html: highlight(updatedValue, languages.sql, "sql"),
              }}
            />
            <CopyButton type="ghost" onClick={onCopy} title="Copy SQL">
              {tooltip}
            </CopyButton>
          </EditorContainer>

          <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
            <WarningIcon style={{ paddingRight: 5 }} />
            This setting should only be used as a temporary solution. We recommend transitioning to
            OAuth or key based authentication before the deadline (August 2025). For details, refer
            to Snowflake’s official documentation.
          </Typography>

          <Button type="primary" onClick={() => onClose(null)}>
            Done
          </Button>
        </FlexColumn>
      </StyledModal>
    );
  }

  return (
    <StyledModal visible footer={null} onCancel={() => onClose(snowflakeKey)} centered>
      {step === Step.createKeyPair && (
        <FlexColumn gap={2}>
          <Typography fontSize={20} fontWeight={600} lineHeight={"22px"}>
            Configure in Snowflake
          </Typography>

          <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
            Generate a new public-private key, or upload an existing private key if you already have
            one.
          </Typography>

          <FlexColumn style={{ marginTop: 20 }}>
            <Button
              type="primary"
              onClick={() => generateKeyPairMutation.mutate()}
              style={{ width: "100%" }}
              disabled={disabled}
            >
              {generateKeyPairMutation.isLoading ? "Generating..." : "Generate new private key"}
            </Button>

            <StyledUpload
              showUploadList={false}
              beforeUpload={handleFileUpload}
              disabled={disabled}
            >
              <Button disabled={disabled} style={{ width: "100%" }} type="default">
                Upload existing private key
              </Button>
            </StyledUpload>

            {uploadError && (
              <ErrorText color="error" fontSize={14}>
                {uploadError}
              </ErrorText>
            )}
          </FlexColumn>
        </FlexColumn>
      )}

      {step === Step.downloadKeyPair && (
        <FlexColumn gap={3}>
          <Typography fontSize={20} lineHeight={"22px"}>
            <CheckCircleFilled style={{ color: COLORS.green2, paddingRight: 10 }} />
            Private Key Generated
          </Typography>

          <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
            Your private key has been automatically downloaded. Please store it securely.
            <br />
            <br />
            You can re-use this key to connect to your Snowflake instance at a later time.
          </Typography>

          <Button type="primary" onClick={() => setStep(Step.configureKeyPair)}>
            Next
          </Button>
        </FlexColumn>
      )}

      {step === Step.enterPassphrase && (
        <FlexColumn gap={2}>
          <Typography fontSize={20} fontWeight={600} lineHeight={"22px"}>
            Private Key Passphrase
          </Typography>

          <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
            Private key provided is encrypted. Provide a passphrase to continue.
          </Typography>

          <Input.Password
            data-1p-ignore
            placeholder="Enter passphrase"
            value={passphrase}
            onChange={e => setPassphrase(e.target.value)}
            disabled={uploadKeyPairMutation.isLoading}
            autoFocus
            onPressEnter={() =>
              uploadKeyPairMutation.mutate({
                privateKey: uploadedPrivateKey,
                passphrase,
                hasError: true,
              })
            }
          />

          {uploadError && (
            <ErrorText color="error" fontSize={14}>
              {uploadError}
            </ErrorText>
          )}

          <FlexColumn style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() =>
                uploadKeyPairMutation.mutate({
                  privateKey: uploadedPrivateKey,
                  passphrase,
                  hasError: true,
                })
              }
              disabled={uploadKeyPairMutation.isLoading}
              style={{ width: "100%" }}
            >
              {uploadKeyPairMutation.isLoading ? "Verifying..." : "Continue"}
            </Button>

            <Button
              type="default"
              onClick={() => {
                setUploadError("");
                setStep(Step.createKeyPair);
              }}
              disabled={uploadKeyPairMutation.isLoading}
              style={{ width: "100%", marginTop: 8 }}
            >
              Back
            </Button>
          </FlexColumn>
        </FlexColumn>
      )}

      {step === Step.configureKeyPair && (
        <FlexColumn gap={1}>
          <Typography fontSize={20} fontWeight={600} lineHeight={"22px"}>
            Configure in Snowflake
          </Typography>
          <EditorContainer>
            <pre
              dangerouslySetInnerHTML={{
                __html: highlight(updatedValue, languages.sql, "sql"),
              }}
            />
            <CopyButton type="ghost" onClick={onCopy}>
              {tooltip}
            </CopyButton>
          </EditorContainer>
          <section>
            <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
              Next:
            </Typography>
            <StyledList>
              <li>
                <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
                  Log in to{" "}
                  <a href="https://app.snowflake.com/" target="_blank">
                    Snowflake
                  </a>{" "}
                  and create a new SQL Worksheet.
                </Typography>
              </li>
              <li>
                <Typography color="textSecondary" fontSize={16} lineHeight={"20px"}>
                  Run the above SQL to register the corresponding public key.
                </Typography>
              </li>
            </StyledList>
          </section>

          <Button type="primary" onClick={() => onClose(snowflakeKey)}>
            Done
          </Button>
        </FlexColumn>
      )}
    </StyledModal>
  );
};
